import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { CostCenterObject } from '~/models/masterdata/CostCenter';
import { type ParentOrganisationalGroup } from '~/models/masterdata/OrganisationalGroup';

import { queryKeysCostCenter } from './queryKeys';

type CostCentersLegacy = {
  companyAccountId: UUID;
  companyId: UUID;
  end: string | undefined;
  start: string | undefined;
  syncKey: string | undefined;
  modifiedOn: string;
  isActive: boolean;
  isSynced?: boolean;
  id: UUID;
  name: string;
  orgUnits: UUID[];
  orgUnitPaths: UUID[][];
  parentOrganizationalUnits: ParentOrganisationalGroup[];
  parentOrganizationalUnitPaths: UUID[];
  orgUnitsSyncKeys: string | undefined;
  type: 'cost_center';
};

type CostCentersLegacyResponse = {
  items: readonly CostCentersLegacy[];
  count: number;
};

/**
 * Fetches cost centers legacy data from the API.
 * @returns {Promise<CompanyLegacy[]>} The company data if successful, null otherwise.
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/get_all_companies_company_all_get
 */

export const fetchCostCentersLegacy = async () => {
  const { items } = await vestigasApi
    .get(ENDPOINT.COST_CENTER.GET_ALL_LEGACY())
    .json<CostCentersLegacyResponse>();

  return items.map((item) => CostCenterObject.create(item));
};

// This query uses an deprecated endpoint for fetching ALL cost centers ever, which is not technically possible for large accounts.
export const useQueryCostCentersLegacy = (
  options?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>,
) =>
  useQuery({
    queryFn: async () => fetchCostCentersLegacy(),
    queryKey: queryKeysCostCenter.getAllLegacy(),
    ...options,
  });
